import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Facebook, Ghost, Music2, Search } from "lucide-react";
import type {
  PaidMediaOverviewPlatform,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewAmount,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import {
  OVERVIEW_PLATFORM_DESCRIPTORS,
  type OverviewPlatformDescriptor,
} from "../../data/paidMediaOverviewPlatforms";
import {
  OverviewMetricTip,
  OverviewShareBar,
  type OverviewShareBarVariant,
} from "./overviewPrimitives";

interface PlatformComparisonCardsProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  currency: string | null;
}

const PLATFORM_ICONS: Record<string, ReactNode> = {
  meta: <Facebook size={16} />,
  google: <Search size={16} />,
  tiktok: <Music2 size={16} />,
  snapchat: <Ghost size={16} />,
};

const PlatformComparisonCards: FC<PlatformComparisonCardsProps> = ({ platforms, currency }) => {
  const summaryFor = (platform: PaidMediaOverviewPlatform) =>
    platforms?.find(entry => entry?.platform === platform);

  return (
    <div className="paid-media-overview__comparison-grid">
      {OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => (
        <PlatformCard
          key={descriptor.key}
          descriptor={descriptor}
          summary={summaryFor(descriptor.key)}
          currency={currency}
        />
      ))}
    </div>
  );
};

const PlatformCard: FC<{
  descriptor: OverviewPlatformDescriptor;
  summary: PaidMediaOverviewSummaryPlatform | undefined;
  currency: string | null;
}> = ({ descriptor, summary, currency }) => {
  const { t } = useTranslation();
  const title = t(descriptor.labelKey);
  const shareVariant = `${descriptor.slug}-ads` as OverviewShareBarVariant;

  return (
    <article className="paid-media-panel paid-media-overview__platform-card">
      <div className="paid-media-overview__platform-card-header">
        <div className="paid-media-overview__platform-card-title">
          <span
            className={`paid-media-overview__platform-card-icon paid-media-overview__platform-card-icon--${descriptor.slug}`}
            aria-hidden
          >
            {PLATFORM_ICONS[descriptor.slug]}
          </span>
          <span>
            {title}
            <OverviewMetricTip id={descriptor.tooltipId} label={title} />
          </span>
        </div>
        <span className="paid-media-overview__platform-card-tag">
          {t("paidMediaOverview.comparison.tag")}
        </span>
      </div>
      <div className="paid-media-overview__platform-card-metrics">
        <Metric
          label={t("paidMediaOverview.metrics.spend")}
          value={formatOverviewAmount(summary?.spend, currency)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.revenue")}
          value={formatOverviewAmount(summary?.revenue, currency)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.purchases")}
          value={formatOverviewNumber(summary?.purchases)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.roas")}
          value={formatOverviewMultiplier(summary?.roas)}
        />
        <Metric
          label={t("paidMediaOverview.metrics.costPerPurchase")}
          value={formatOverviewUnitCost(summary?.cost_per_purchase, currency)}
        />
      </div>
      <div className="paid-media-overview__platform-card-shares">
        <OverviewShareBar
          label={t("paidMediaOverview.comparison.shareOfTotalSpend")}
          value={summary?.spend_share ?? null}
          variant={shareVariant}
          tooltipId="spendShare"
        />
        <OverviewShareBar
          label={t("paidMediaOverview.comparison.shareOfPaidRevenue")}
          value={summary?.revenue_share ?? null}
          variant={shareVariant}
          tooltipId="revenueShare"
        />
      </div>
    </article>
  );
};

const Metric: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="paid-media-overview__metric">
    <div className="paid-media-overview__metric-label">{label}</div>
    <div className="paid-media-overview__metric-value">{value}</div>
  </div>
);

export default PlatformComparisonCards;
